import {
  useFindAllSoftwareQuery,
  useFindOneLicenseQuery,
  useRemoveLicenseMutation,
  useFindAllHardwareQuery,
  useFindAllModulesQuery,
  useFindAllLicensesQuery,
} from '../../api/api-hooks';
import { Grid, TextField, Typography } from '@mui/material';
import { Autocomplete } from '@mui/material';
import { nullStringCheck } from '../../utils/stringManipulation';
import { Item } from '../../utils/sharedTypes';
import { Order } from '../../api/api-generated';
import useLicensesItemCreator from './useLicensesItemCreator';
import useLicensesItemUpdater from './useLicensesItemUpdater';
import React, { useEffect, useReducer } from 'react';
import LicensesTable from './LicensesTable';
import ItemOptionsBarWithLinking from '../../components/ItemOptionsBarWithLinking';
import { LicenseFilter, LicenseForm } from './licensesTypes';
import { licensesMiddleware, licenseValidator } from './licensesUtils';
import ModulesManagementButton from '../modules/ModulesManagementButton';
import LicensesFilterBar from './LicensesFilterBar';

interface Props {
  selectedLicenses: Item[];
  setSelectedLicenses: React.Dispatch<React.SetStateAction<Item[]>>;
  order: Order;
}

const initialFilters: LicenseFilter = {
  software: null,
  hardware: null,
  module: null,
};

const addFormInitialState: LicenseForm = {
  order: null,
  software: null,
  company: null,
  hardware: null,
  module: null,
  license: '',
};

const LicensesOptionsBar = ({
  selectedLicenses,
  setSelectedLicenses,
  order,
}: Props) => {
  const [filters, setFilters] = useReducer(
    (curVals: LicenseFilter, newVals: any) => ({
      ...curVals,
      ...newVals,
    }),
    initialFilters
  );
  const [deleteLicense] = useRemoveLicenseMutation();
  const itemCreator = useLicensesItemCreator();
  const itemUpdater = useLicensesItemUpdater();
  const { data: allSoftwareTypes } = useFindAllSoftwareQuery({});
  const { data: allHardware } = useFindAllHardwareQuery({});
  const { data: allModules } = useFindAllModulesQuery({});
  const { data: allLicenses } = useFindAllLicensesQuery({});
  useEffect(() => {
    addFormInitialState.order = order;
    addFormInitialState.company = order.company;
  }, [order]);
  if (
    allSoftwareTypes === undefined ||
    allHardware === undefined ||
    allModules === undefined ||
    allLicenses === undefined
  )
    return null;

  return (
    <>
      <ItemOptionsBarWithLinking
        selectedItems={selectedLicenses}
        setSelectedItems={setSelectedLicenses}
        handlerMiddleware={licensesMiddleware}
        itemValidator={licenseValidator}
        formInstructions='Software, hardware, and license are required.'
        addFormInitialState={addFormInitialState}
        itemName='License'
        itemToLink={order}
        itemToLinkName='order'
        itemToLinkIdName='orderId'
        itemToLinkTitle='Order'
        tableCreator={(setSelectedLicenses) => (
          <>
            <LicensesFilterBar
              filters={filters}
              setFilters={setFilters}
              initialFilters={initialFilters}
            />
            <LicensesTable
              setSelectedLicenses={setSelectedLicenses}
              extraFilters={{ order: order, excludeOrder: true }}
              filters={filters}
            />
          </>
        )}
        childrenCreator={(addFormState: LicenseForm, handler: any) => (
          <Grid container direction='column' spacing={1}>
            <Grid item>
              <Autocomplete
                onChange={(event, value) => {
                  handler(event, value, 'software');
                }}
                value={addFormState.software}
                options={allSoftwareTypes}
                getOptionLabel={(type) => type.type}
                style={{ width: 300, marginTop: '.5rem' }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label='Select Software'
                    variant='outlined'
                  />
                )}
              />
            </Grid>
            <Grid item>
              <Autocomplete
                onChange={(event, value) => {
                  handler(event, value, 'hardware');
                }}
                value={addFormState.hardware}
                options={allHardware}
                getOptionLabel={(type) => type.serial}
                style={{ width: 300 }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label='Select Hardware'
                    variant='outlined'
                  />
                )}
              />
            </Grid>
            {/* If the user wants to add a Spectras license, force the creation of a default license first 
            if none exists; if a default license does exist, force the creation of modules */}
            {addFormState.software?.type.toLowerCase().includes('spectras') &&
            addFormState.software &&
            addFormState.hardware &&
            addFormState.company &&
            !allLicenses.find(
              (license) =>
                license.softwareId === addFormState.software?.id &&
                license.companyId === addFormState.company?.id &&
                license.hardwareId === addFormState.hardware?.id &&
                !license.moduleId
            ) ? (
              <Grid item>
                <Typography color={'error'}>
                  The selected hardware does not have an associated non-module
                  Spectras license, which is required before adding modules.
                  Please add a Spectras license without a module selected via
                  the below Add button and then click Save, closing and
                  reopening this dialog, before before adding any modules.
                </Typography>
              </Grid>
            ) : (
              // Only display the modules field if there is a default Spectras license for the selected hardware and the user has selected Spectras
              addFormState.software?.type.toLowerCase().includes('spectras') &&
              addFormState.hardware && (
                <>
                  <Grid item>
                    <Autocomplete
                      onChange={(event, value) => {
                        handler(event, value, 'module');
                      }}
                      value={addFormState.module}
                      options={allModules}
                      getOptionLabel={(module) => module.moduleName}
                      style={{ width: 300 }}
                      disabled={
                        !addFormState.hardware || !addFormState.software
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label='Select Module'
                          variant='outlined'
                        />
                      )}
                    />
                  </Grid>
                  <Grid item>
                    <ModulesManagementButton />
                  </Grid>
                </>
              )
            )}
            <Grid item>
              <TextField
                margin='dense'
                name='license'
                fullWidth
                label='License'
                value={nullStringCheck(addFormState.license)}
                onChange={handler}
              />
            </Grid>
          </Grid>
        )}
        itemCreator={itemCreator}
        itemGetter={useFindOneLicenseQuery}
        itemUpdater={itemUpdater}
        itemDeleter={deleteLicense}
      />
    </>
  );
};

export default LicensesOptionsBar;
